import axios from "axios";
import { useUserStore } from "@/store/user";
import { usePackageStore } from "@/store/package";
import { useEventTracking } from "@/hooks/useEventTracking";
import { validateMemberPurchase } from "./purchaseValidation";
import { isMemberUpgradePurchase, resolveMemberPurchaseFlags } from "./purchaseIntent";
import { UPGRADE_TRACK_BUTTON } from "./purchase.constants";
import type { MemberPurchaseInput, MemberPurchaseStore, MemberSource } from "./purchase.types";

export { isMemberUpgradePurchase } from "./purchaseIntent";

/** 所有个人会员入口共享互斥、权益复核和来源生命周期检查。 */
export async function openMemberPurchase(store: MemberPurchaseStore, input: MemberPurchaseInput): Promise<void> {
  if (store.memberPurchasePending) return;
  store.memberPurchasePending = true;
  const user = useUserStore();
  const userId = user.userInfo.id;
  const isActive = () => input.isActive?.() !== false && user.isLogin && user.userInfo.id === userId;
  try {
    await user.queryUserRights();
    if (!isActive()) return;
    const action = validateMemberPurchase(input, user);
    const purchase = { ...input, ...resolveMemberPurchaseFlags(input, action) };
    Object.assign(store.payModalState, {
      payMode: "member",
      memberPackage: input.key,
      timeMode: input.timeMode,
      trialDeclaration: input.trialDeclaration,
      isBuyout: input.isBuyout,
      subscribePlan: purchase.subscribePlan
    });
    store.ballParams.button = store.isOpenFromQueueSpeed ? "button_queue_speed" : "button_buy_member";
    store.isOpenFromQueueSpeed = false;
    store.isUpgrade = input.trialDeclaration !== true && !purchase.skipUpgrade &&
      Boolean(purchase.forceUpgrade || isMemberUpgradePurchase(input.key, user.highestPackage));
    if (store.isUpgrade && !(await prepareUpgrade(store, purchase, isActive))) return;
    if (isActive()) store.payModalState.open = true;
  } finally {
    store.memberPurchasePending = false;
  }
}

async function prepareUpgrade(store: MemberPurchaseStore, input: MemberPurchaseInput, isActive: () => boolean) {
  trackUpgrade(input.source ?? "powerPage");
  try {
    const refund = await store.fetchUpgradeRefundForTarget(input.key, input.timeMode, input.isBuyout);
    if (!isActive()) return false;
    store.refundInfo = refund;
    if (refund.upStatus === 1) {
      const packageStore = usePackageStore();
      packageStore.upgradeDisabledModalType = useUserStore().currentMemberInfo.type;
      packageStore.upgradeDisabledModalOpen = true;
      return false;
    }
  } catch (error) {
    if (axios.isCancel(error) || (error instanceof Error && /cancel/i.test(error.message))) return false;
    throw error;
  }
  if (input.isConfirmUpgrade) return true;
  await store.openMemberUpgradePlanModal(isActive);
  return false;
}

function trackUpgrade(source: MemberSource) {
  useEventTracking({ event: "click", eventValue: { button: UPGRADE_TRACK_BUTTON[source] } });
}
